import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { Link } from 'react-router-dom';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import EngineeringIcon from '@mui/icons-material/Engineering';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import EventNoteIcon from '@mui/icons-material/EventNote';
import frankyTitle from '../../assets/franky-water7.webp';
import frankyCorner from '../../assets/franky-post.webp';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { getUser } from '../../auth/authService';
import { isFinanceUser } from '../../auth/financeAccess';
import './Landing.css';

// Each Franky portrait on the Landing page goes to its own SUPER clip; the
// NavBar brand Franky uses a third one (NavBar.js → FRANKY_CLIPS.brand).
const FRANKY_TITLE_URL  = 'https://www.youtube.com/watch?v=IvlWP4lQ6m8'; // "One Piece: Franky SUPER!" — Ep. 370 Thriller Bark
const FRANKY_CORNER_URL = 'https://www.youtube.com/watch?v=1qDOEQsaTak';

const SectionCard = ({ icon, title, description, to }) => (
    <Paper
        component={Link}
        to={to}
        className="landing-card d-hover"
        elevation={0}
    >
        <Box className="cc" sx={{ flexDirection: 'column', gap: 2 }}>
            {icon}
            <Typography className="nexus-font lg">{title}</Typography>
            <Typography className="d-font md landing-card-desc">
                {description}
            </Typography>
        </Box>
    </Paper>
);

const Landing = () => {
    // Finance tile is shown only to finance users (see auth/financeAccess.js); the
    // analytics API enforces the same rule server-side.
    const [showFinance, setShowFinance] = useState(false);
    useEffect(() => {
        getUser().then((user) => setShowFinance(isFinanceUser(user?.profile))).catch(() => {});
    }, []);

    return (
    <Box className="landing-container">
        <Box className="landing-header">
            <Box className="landing-title-row">
                <Typography className="nexus-font lg" sx={{ mb: 0.5 }}>
                    Welcome to Nexus Super
                </Typography>
                <a
                    href={FRANKY_TITLE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="SUUUPEEER!"
                    title="SUUUPEEER!"
                    className="franky-link"
                >
                    <img
                        src={frankyTitle}
                        alt="SUUUPEEER!"
                        className="franky-icon franky-title"
                    />
                </a>
            </Box>
            <Typography className="d-font md" sx={{ color: '#666' }}>
                Select a workspace to get started.
            </Typography>
        </Box>
        <Box className="landing-cards">
            <SectionCard
                icon={<AdminPanelSettingsIcon sx={{ fontSize: 52, color: '#004df2' }} />}
                title="Admin"
                description="Manage modularities and per-client, per-user feature access."
                to="/admin"
            />
            <SectionCard
                icon={<SupportAgentIcon sx={{ fontSize: 52, color: '#004df2' }} />}
                title="Customer Success"
                description="Client management, health monitoring, and wins tracking."
                to="/cs"
            />
            <SectionCard
                icon={<EngineeringIcon sx={{ fontSize: 52, color: '#004df2' }} />}
                title="Implementations"
                description="Manage and track client implementation projects."
                to="/implementations"
            />
            <SectionCard
                icon={<EventNoteIcon sx={{ fontSize: 52, color: '#004df2' }} />}
                title="Meetings"
                description="Every client meeting on the team's calendars, and the work that came out of them."
                to="/meetings"
            />
            <SectionCard
                icon={<DashboardCustomizeIcon sx={{ fontSize: 52, color: '#004df2' }} />}
                title="Work Overview"
                description="Every piece of client-sourced work — DevOps, outreach, to-dos and requests — in one place."
                to="/work"
            />
            {showFinance && (
                <SectionCard
                    icon={<AccountBalanceIcon sx={{ fontSize: 52, color: '#004df2' }} />}
                    title="Finance"
                    description="Azure, AI API, Vonage, and subscription spend — the nightly cost dashboard."
                    to="/finance"
                />
            )}
        </Box>
        <a
            href={FRANKY_CORNER_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="SUUUPEEER!"
            title="SUUUPEEER!"
            className="franky-link franky-corner-link"
        >
            <img
                src={frankyCorner}
                alt="SUUUPEEER!"
                className="franky-icon franky-corner"
            />
        </a>
    </Box>
    );
};

export default Landing;
